import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Search, ArrowLeft, Sparkles, Music } from 'lucide-react';
import EventCard from './EventCard';
import VenueCard from './VenueCard';

/**
 * Full-screen list of all events OR all venues, with a floating "liquid-glass"
 * search FAB at the bottom-right that expands into a search bar.
 */
const AllItemsScreen = ({ isOpen, mode, events, venues, onClose, onEventClick, onVenueEvents }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchOpen(false);
      setQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [searchOpen]);

  const isEvents = mode === 'events';
  const title = isEvents ? 'Todos los eventos' : 'Todas las salas';
  const Icon = isEvents ? Sparkles : Music;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (isEvents) {
      const list = events || [];
      if (!q) return list;
      return list.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          (e.venue || '').toLowerCase().includes(q) ||
          (e.location || '').toLowerCase().includes(q) ||
          (e.day || '').toLowerCase().includes(q)
      );
    }
    const list = venues || [];
    if (!q) return list;
    return list.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        (v.location || '').toLowerCase().includes(q)
    );
  }, [isEvents, events, venues, query]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] bg-[#0a0a0a] flex flex-col" data-testid="all-items-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/5 px-5 py-4 flex items-center gap-3">
        <button
          onClick={onClose}
          className="glass-dark w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          data-testid="all-items-close"
          aria-label="Cerrar"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-2">
          <Icon size={20} className="text-[#F82C47]" />
          <h2 className="text-white text-xl font-bold">{title}</h2>
        </div>
        <span className="ml-auto text-gray-500 text-sm" data-testid="all-items-count">{filtered.length}</span>
      </div>

      {/* Active query chip */}
      {query.trim() && (
        <div className="px-5 pt-3">
          <div className="inline-flex items-center gap-2 glass-dark text-white text-sm rounded-full px-3 py-1.5">
            <Search size={13} className="text-white" />
            <span className="truncate max-w-[200px]">{query}</span>
            <button onClick={() => setQuery('')} className="text-white/70 hover:text-white" data-testid="all-items-clear-query">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-32">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Search size={48} className="text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Sin resultados {query ? `para "${query}"` : ''}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isEvents
              ? filtered.map((e) => (
                  <div key={e.id} className="w-full">
                    <EventCardFluid event={e} onClick={onEventClick} />
                  </div>
                ))
              : filtered.map((v) => (
                  <div key={v.id} className="w-full">
                    <VenueCardFluid venue={v} onViewEvents={onVenueEvents} />
                  </div>
                ))}
          </div>
        )}
      </div>

      {/* Liquid-glass Search FAB — Apple Music style */}
      <div className="fixed bottom-6 right-5 z-[120]">
        {!searchOpen ? (
          <button
            onClick={() => setSearchOpen(true)}
            className="glass-dark w-16 h-16 rounded-full flex items-center justify-center text-white shadow-2xl shadow-black/50 active:scale-95 transition-transform duration-200"
            data-testid="all-items-search-fab"
            aria-label="Buscar"
          >
            <Search size={26} className="text-white" strokeWidth={2.2} />
          </button>
        ) : (
          <div className="flex items-center gap-2 animate-fab-expand" style={{ transformOrigin: 'right center' }}>
            <div
              className="glass-dark flex items-center gap-3 rounded-full px-4 py-3 shadow-2xl shadow-black/50"
              style={{ minWidth: 'min(75vw, 320px)' }}
            >
              <Search size={18} className="text-white shrink-0" strokeWidth={2.2} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Busca clubs, eventos, viajes…"
                className="flex-1 bg-transparent text-white placeholder-white/55 text-base outline-none border-none py-0.5 caret-[#F82C47]"
                data-testid="all-items-search-input"
              />
            </div>
            <button
              onClick={() => { setQuery(''); setSearchOpen(false); }}
              className="glass-dark w-12 h-12 rounded-full flex items-center justify-center text-white shrink-0 shadow-2xl shadow-black/50 active:scale-95 transition-transform"
              data-testid="all-items-search-close"
              aria-label="Cerrar búsqueda"
            >
              <X size={20} strokeWidth={2.2} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Full-width variants of the cards (instead of fixed min-width 280px) so they fit the grid
const EventCardFluid = ({ event, onClick }) => (
  <div className="w-full">
    <div style={{ width: '100%' }}>
      <FluidWrapper>
        <EventCard event={event} onClick={onClick} />
      </FluidWrapper>
    </div>
  </div>
);

const VenueCardFluid = ({ venue, onViewEvents }) => (
  <div className="w-full">
    <FluidWrapper>
      <VenueCard venue={venue} onViewEvents={onViewEvents} />
    </FluidWrapper>
  </div>
);

// Wrapper that overrides the carousel min-width so cards fill grid cells
const FluidWrapper = ({ children }) => (
  <div className="[&>div]:!min-w-0 [&>div]:!max-w-none [&>div]:w-full">{children}</div>
);

export default AllItemsScreen;
